import type {
  ApiResponseSimple,
  DigestJobResult,
  ReportChartJobIdResponse,
  ReportFeedbackResponseCollection,
  ReportFeedbackStaticResponse,
} from '@vision/ui/interfaces';
import type { TableDispatchResultDto } from './table-dispatch.api.types';
import { fromTableDispatchGroupDto, fromTableDispatchMetricDto } from './table-dispatch.service.mapper';

type ReportResultDto = ReportFeedbackResponseCollection | TableDispatchResultDto;
type ReportResponseDto = ReportChartJobIdResponse | ApiResponseSimple<ReportResultDto>;
type ReportStaticResponseDto = Omit<ReportFeedbackStaticResponse, 'result'> & {
  result: Omit<ReportFeedbackStaticResponse['result'], 'data'> & { data: ReportResultDto };
};
type CachedReportJobResultDto = Omit<DigestJobResult, 'details'> & {
  details: (Omit<DigestJobResult['details'][number], 'result'> & {
    result?: ReportStaticResponseDto['result'];
  })[];
};

export function fromReportResultDto(data: ReportResultDto): ReportFeedbackResponseCollection {
  if ('groups' in data) {
    return {
      groups: data.groups.map((group) => ('field_code' in group ? fromTableDispatchGroupDto(group) : group)),
      overall:
        data.overall && ('feedback_count' in data.overall ? fromTableDispatchMetricDto(data.overall) : data.overall),
    };
  }

  return data;
}

// The shared reporting envelope is legacy; normalize only the Table Dispatch payload.
export function fromReportResponseDto(
  response: ReportResponseDto,
): ReportChartJobIdResponse | ApiResponseSimple<ReportFeedbackResponseCollection> {
  return 'job_id' in response ? response : { ...response, data: fromReportResultDto(response.data) };
}

export function fromReportStaticResponseDto(response: ReportStaticResponseDto): ReportFeedbackStaticResponse {
  return {
    ...response,
    result: response.result && {
      ...response.result,
      data: response.result.data && fromReportResultDto(response.result.data),
    },
  };
}

export function fromCachedReportResults(results: CachedReportJobResultDto[]): DigestJobResult[] {
  return results.map((result) => ({
    ...result,
    details: result.details.map((detail) => ({
      ...detail,
      ...(detail.result && {
        result: { ...detail.result, data: detail.result.data && fromReportResultDto(detail.result.data) },
      }),
    })),
  }));
}
